import React from 'react';
import { useI18n } from '../context/I18nContext';
import { Link } from 'react-router-dom';
import { LogoIcon, UsersIcon, CheckIcon } from '../components/Icons';
import { useSettings } from '../context/SettingsContext';

const About = () => {
  const { settings, getCityState } = useSettings();

  const values = [
    {
      title: 'Inovacao',
      description: t('about.a1')
    },
    {
      title: 'Simplicidade',
      description: t('about.a2')
    },
    {
      title: 'Confianca',
      description: t('about.a3')
    },
    {
      title: 'Excelencia',
      description: t('about.a4')
    }
  ];

  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <LogoIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">{settings.companyName}</span>
          </Link>
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">{t('about.j1')}</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-500 to-indigo-600 py-20">
        <div className="max-w-5xl mx-auto px-6 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Sobre a {settings.companyName}</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">{t('about.j2')}</p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Nossa Historia */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('about.ourStory')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t('about.story1', { v1: settings.companyName })}</p>
                <p>{t('about.j3')}</p>
              </div>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t('about.j4')}</p>
                <p>{t('about.j5')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Nossos Valores */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('about.ourValues')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <CheckIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-sm text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Numeros */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-8 text-center">{settings.companyName} em Numeros</h2>
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">500+</div>
                <div className="text-white/80">{t('about.companiesServed')}</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">1M+</div>
                <div className="text-white/80">{t('about.messagesSent')}</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">99.9%</div>
                <div className="text-white/80">{t('about.uptime')}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Equipe */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <UsersIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{t('about.ourTeam')}</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">{t('about.j6')}</p>
          </div>
        </section>

        {/* Empresa */}
        <section>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('about.companyInfo')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('about.legalName')}</h3>
                  <p className="text-gray-900">{settings.companyName}</p>
                </div>
                {settings.cnpj && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('about.cnpj')}</h3>
                    <p className="text-gray-900">{settings.cnpj}</p>
                  </div>
                )}
                {settings.address && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('about.addr')}</h3>
                    <p className="text-gray-900">
                      {settings.address}
                      {getCityState() && <><br />{getCityState()}</>}
                      {settings.zipCode && <> - CEP {settings.zipCode}</>}
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {settings.email && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('about.email')}</h3>
                    <p className="text-gray-900">{settings.email}</p>
                  </div>
                )}
                {settings.phone && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('about.phone')}</h3>
                    <p className="text-gray-900">{settings.phone}</p>
                  </div>
                )}
                {settings.website && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('about.website')}</h3>
                    <p className="text-gray-900">
                      <a href={settings.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {settings.website}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-6">
        <div className="max-w-5xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} {settings.companyName}. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
};

export default About;
